import { useEffect, useState } from "react";
import { diffWords } from "diff";
import { Lock, ShieldCheck, ShieldAlert, Eye, GitCompare, ArrowLeft, RotateCcw } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { listVersions, verifyVersion, versionPatch, differsFrom } from "@/lib/store";

function VersionView({ v }) {
  const r = Object.entries(v.receipt || {}).filter(([, x]) => x);
  return (
    <div className="space-y-3" data-testid="version-view">
      <Badge className="gap-1"><Lock className="h-3 w-3" />Version {v.n} • read-only</Badge>
      <h3 className="font-heading text-xl font-semibold">{v.title || "Untitled"}</h3>
      <p className="font-mono text-xs text-muted-foreground">{v.localTime} {v.tz} • Ref {v.ref || "—"} • {v.folder}</p>
      {r.length > 0 && <p className="font-mono text-xs">{r.map(([k, x]) => `${k}: ${x}`).join(" • ")}</p>}
      <pre className="whitespace-pre-wrap text-sm rounded-md border bg-muted/40 p-4 font-sans select-text">{v.body || "(empty)"}</pre>
      <p className="text-xs">Attachments at seal: {v.attachments.length ? v.attachments.map((a) => a.name).join(", ") : "none"}</p>
      <p className="font-mono text-[10px] break-all text-muted-foreground">SHA-256 {v.hash}</p>
      <p className="font-mono text-[10px] break-all text-muted-foreground">Prev {v.prevHash || "— (genesis)"}</p>
    </div>
  );
}

function Compare({ a, b }) {
  const parts = diffWords(`${a.title}\n\n${a.body}`, `${b.title}\n\n${b.body}`);
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="version-compare">
      {[[a, "removed", "bg-destructive/25 line-through"], [b, "added", "bg-verified/25"]].map(([v, key, cls]) => (
        <div key={key} className="rounded-md border p-3">
          <p className="font-mono text-xs mb-2 text-primary">Version {v.n} • {v.localTime}</p>
          <pre className="whitespace-pre-wrap text-sm font-sans">
            {parts.filter((p) => !p[key === "removed" ? "added" : "removed"]).map((p, i) => <span key={i} className={p[key] ? cls : ""}>{p.value}</span>)}
          </pre>
        </div>
      ))}
    </div>
  );
}

function Row({ v, picked, onPick, onView, onRestore }) {
  const [ok, setOk] = useState(null);
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid={`version-row-${v.n}`}>
      <div className="flex items-center gap-2">
        <Checkbox checked={picked} onCheckedChange={onPick} data-testid={`version-compare-check-${v.n}`} />
        <Lock className="h-3.5 w-3.5 text-primary" />
        <span className="font-heading font-semibold">Version {v.n}</span>
        {v.importedAt && <Badge variant="secondary" className="text-[10px]">synced</Badge>}
        <span className="ml-auto font-mono text-[11px] text-muted-foreground">{v.localTime} {v.tz}</span>
      </div>
      <p className="font-mono text-[10px] tracking-wider break-all text-muted-foreground" data-testid={`version-hash-${v.n}`}>{v.hash}</p>
      <p className="text-xs text-muted-foreground truncate">{v.creatorStamp}</p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onView} data-testid={`version-view-${v.n}`}><Eye className="h-3 w-3 mr-1" />View</Button>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onRestore} data-testid={`version-restore-${v.n}`}><RotateCcw className="h-3 w-3 mr-1" />Restore & Edit</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={async () => setOk(await verifyVersion(v))} data-testid={`version-verify-${v.n}`}>Verify hash</Button>
        {ok !== null && (ok
          ? <Badge className="gap-1 bg-verified text-background" data-testid={`version-verified-${v.n}`}><ShieldCheck className="h-3 w-3" />Intact</Badge>
          : <Badge variant="destructive" className="gap-1" data-testid={`version-tampered-${v.n}`}><ShieldAlert className="h-3 w-3" />Mismatch</Badge>)}
      </div>
    </div>
  );
}

function RestoreConfirm({ v, onCancel, onConfirm }) {
  return (
    <AlertDialog open={!!v} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="restore-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Replace unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>Your editor has changes that aren't locked yet. Restoring Version {v?.n} will replace them. Sealed versions stay untouched.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="restore-cancel-button">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm(v)} data-testid="restore-confirm-button">Restore Version {v?.n}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function VersionDrawer({ note, open, onOpenChange, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [pick, setPick] = useState([]);
  const [mode, setMode] = useState(null);
  const [ask, setAsk] = useState(null);
  const restore = (v) => { setAsk(null); onRestore(versionPatch(v), v.n); onOpenChange(false); };
  const latest = versions[versions.length - 1];
  const tryRestore = (v) => (latest && differsFrom(note, latest) ? setAsk(v) : restore(v));
  useEffect(() => { if (open) { listVersions(note.id).then(setVersions); setMode(null); setPick([]); } }, [open, note.id, note.versionCount]);
  const toggle = (id) => setPick((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p.slice(-1), id]));
  const picked = versions.filter((v) => pick.includes(v.id));
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="version-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading">Version History</SheetTitle>
          <SheetDescription data-testid="version-history-description">Saved snapshots are locked & sealed—you can restore and edit any version to create a new update.</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          {mode && <Button size="sm" variant="ghost" onClick={() => setMode(null)} data-testid="version-back-button"><ArrowLeft className="h-4 w-4 mr-1" />All versions</Button>}
          {mode?.view && <VersionView v={mode.view} />}
          {mode?.compare && <Compare a={mode.compare[0]} b={mode.compare[1]} />}
          {!mode && (
            <>
              <Button size="sm" disabled={picked.length !== 2} onClick={() => setMode({ compare: picked })} data-testid="version-compare-button">
                <GitCompare className="h-4 w-4 mr-1" />Compare selected ({picked.length}/2)
              </Button>
              {[...versions].reverse().map((v) => <Row key={v.id} v={v} picked={pick.includes(v.id)} onPick={() => toggle(v.id)} onView={() => setMode({ view: v })} onRestore={() => tryRestore(v)} />)}
              {!versions.length && <p className="text-sm text-muted-foreground" data-testid="no-versions">No versions yet. Click “Save & Stamp Note” to seal Version 1.</p>}
            </>
          )}
        </div>
        <RestoreConfirm v={ask} onCancel={() => setAsk(null)} onConfirm={restore} />
      </SheetContent>
    </Sheet>
  );
}
